import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {z} from 'zod';
import {ExcelToCampaignAgentToolName} from '../../excelToCampaignAgent.enums';
import buildSanitizeValuesToolResponse from './utils/buildSanitizeValuesToolResponse';
import getCellsThatNeedToBeSanitized from './utils/getCellsThatNeedToBeSanitized';
import getStartAndEndDatePairs from './utils/getStartAndEndDatePairs';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';

const inputSchema = z.object({
  fileId: z.string(),
  worksheetIndex: z.number().describe('The index of the worksheet to read.'),
  firstDataRowIndex: z.number().describe('The index of the first data row to read.'),
  startDateColumnIndex: z.number().describe('The index of the start date column to read.'),
  endDateColumnIndex: z.number().describe('The index of the end date column to read.'),
});

function sanitizeValuesTool() {
  async function execute({
    toolUtils,
    input: {fileId, worksheetIndex, firstDataRowIndex, startDateColumnIndex, endDateColumnIndex},
    abortSignal,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      // get start and end date pairs to be checked for sanitization
      const startAndEndDatePairs = await getStartAndEndDatePairs({
        toolUtils,
        fileId,
        worksheetIndex,
        firstDataRowIndex,
        startDateColumnIndex,
        endDateColumnIndex,
      });

      // invoke the LLM to determine the cells that need to be sanitized
      const cellsThatNeedToBeSanitized = await getCellsThatNeedToBeSanitized({
        startAndEndDatePairs,
        startDateColumnIndex,
        endDateColumnIndex,
        abortSignal,
      });

      // prepare the response, which includes instructions on how to sanitize the cells
      const response = buildSanitizeValuesToolResponse(cellsThatNeedToBeSanitized);

      return toolResult(response);
    } catch (error) {
      return toolResult(error.message);
    }
  }

  return tool({
    name: ExcelToCampaignAgentToolName.SanitizeValues,
    description: 'Sanitize values in the excel worksheet',
    displayName: 'Sanitizing values',
    execute,
    inputSchema,
  });
}

export default sanitizeValuesTool;
